import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const PrivacyPolicyDialog = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      maxWidth="md"
      fullWidth
      aria-labelledby="privacy-policy-dialog-title"
    >
      <DialogTitle
        id="privacy-policy-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" component="h2">
          Privacy Policy
        </Typography>
        <IconButton aria-label="close" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        
        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            1. Introduction
          </Typography>
          <Typography paragraph>
            Thank you for choosing our AI-powered hiring platform. This Privacy
            Policy explains how we collect, use, disclose, and safeguard your
            information when you use our service. We are committed to protecting
            your privacy and handling your data with transparency and care.
          </Typography>
          <Typography paragraph>
            By accessing or using our platform, you agree to the collection and
            use of information in accordance with this policy. If you do not
            agree with our policies, please do not access or use our services.
          </Typography>
        </section>

        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            2. Information We Collect
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            2.1 Information You Provide
          </Typography>
          <Typography paragraph>
            We collect information that you voluntarily provide when using our
            platform, including:
          </Typography>
          <ul style={{ paddingLeft: "1.5rem", marginBottom: "1rem" }}>
            <li>
              Account information (name, email address, password, company
              details)
            </li>
            <li>
              Profile information (job titles, professional experience, skills)
            </li>
            <li>Job posting details and requirements</li>
            <li>Communications with our support team</li>
            <li>Payment and billing information</li>
          </ul>

          <Typography variant="subtitle1" gutterBottom>
            2.2 Information We Collect Automatically
          </Typography>
          <Typography paragraph>
            When you access our platform, we automatically collect certain
            information, including:
          </Typography>
          <ul style={{ paddingLeft: "1.5rem", marginBottom: "1rem" }}>
            <li>Usage data (pages visited, features used, time spent)</li>
            <li>
              Device information (IP address, browser type, operating system)
            </li>
            <li>Cookies and similar tracking technologies</li>
            <li>Performance and error data</li>
          </ul>

          <Typography variant="subtitle1" gutterBottom>
            2.3 AI Processing Data
          </Typography>
          <Typography paragraph>
            Our platform uses artificial intelligence to process application
            materials, candidate profiles, and job descriptions. This may
            include analysis of resumes, cover letters, job descriptions, and
            other materials submitted to our platform.
          </Typography>
        </section>

        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            3. How We Use Your Information
          </Typography>
          <Typography paragraph>
            We use the information we collect for various purposes, including:
          </Typography>
          <ul style={{ paddingLeft: "1.5rem", marginBottom: "1rem" }}>
            <li>Providing and maintaining our services</li>
            <li>Enhancing and personalizing your experience</li>
            <li>Matching candidates with appropriate job opportunities</li>
            <li>Processing transactions and managing your account</li>
            <li>Communicating with you about service-related matters</li>
            <li>Analyzing usage patterns to improve our platform</li>
            <li>Training and improving our AI systems</li>
            <li>Ensuring compliance with legal obligations</li>
            <li>
              Detecting and preventing fraudulent or unauthorized activities
            </li>
          </ul>
        </section>

        {/* Including abbreviated content for brevity */}
        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            4. Data Sharing and Disclosure
          </Typography>
          <Typography paragraph>
            We may share your information with third parties in the following
            circumstances:
          </Typography>
          <ul style={{ paddingLeft: "1.5rem", marginBottom: "1rem" }}>
            <li>With service providers who perform services on our behalf</li>
            <li>
              With recruiters and employers when you apply for job positions
              (with your consent)
            </li>
            <li>To comply with legal obligations</li>
            <li>In connection with a business transfer or transaction</li>
            <li>With your consent or at your direction</li>
          </ul>
          <Typography paragraph>
            We do not sell your personal information to third parties for
            marketing purposes.
          </Typography>
        </section>
        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            5. AI and Algorithm Transparency
          </Typography>
          <Typography paragraph>
            Our platform uses AI and machine learning algorithms to match
            candidates with job opportunities. We are committed to algorithmic
            transparency and take measures to prevent discrimination and bias in
            our systems. We regularly audit our algorithms and make adjustments
            to ensure fair and equitable outcomes for all users.
          </Typography>
          <Typography paragraph>
            The AI systems process data to identify patterns and correlations
            but final hiring decisions remain the responsibility of human
            recruiters and employers.
          </Typography>
        </section>

        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            6. Data Security
          </Typography>
          <Typography paragraph>
            We implement appropriate technical and organizational measures to
            protect your personal information against unauthorized access,
            alteration, disclosure, or destruction. These measures include
            encryption, access controls, regular security assessments, and
            employee training on data protection.
          </Typography>
          <Typography paragraph>
            While we strive to use commercially acceptable means to protect your
            personal information, we cannot guarantee its absolute security. Any
            transmission of personal information is at your own risk.
          </Typography>
        </section>

        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            7. Data Retention
          </Typography>
          <Typography paragraph>
            We retain your personal information for as long as necessary to
            fulfill the purposes outlined in this Privacy Policy, unless a
            longer retention period is required or permitted by law. When
            determining the retention period, we consider the amount, nature,
            and sensitivity of the information, the potential risk of harm from
            unauthorized use or disclosure, and applicable legal requirements.
          </Typography>
          <Typography paragraph>
            You may request deletion of your account and personal information at
            any time, subject to legitimate business or legal purposes that may
            require us to maintain certain information.
          </Typography>
        </section>

        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            8. Your Privacy Rights
          </Typography>
          <Typography paragraph>
            Depending on your location, you may have certain rights regarding
            your personal information, including:
          </Typography>
          <ul style={{ paddingLeft: "1.5rem", marginBottom: "1rem" }}>
            <li>
              Right to access and receive a copy of your personal information
            </li>
            <li>Right to correct inaccurate or incomplete information</li>
            <li>Right to delete your personal information</li>
            <li>Right to restrict or object to processing</li>
            <li>Right to data portability</li>
            <li>Right to withdraw consent</li>
            <li>Right to lodge a complaint with a supervisory authority</li>
          </ul>
          <Typography paragraph>
            To exercise your rights, please contact us using the information
            provided in the "Contact Us" section.
          </Typography>
        </section>

        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            9. International Data Transfers
          </Typography>
          <Typography paragraph>
            Your information may be transferred to, and processed in, countries
            other than the country in which you reside. These countries may have
            data protection laws that differ from the laws of your country.
          </Typography>
          <Typography paragraph>
            When we transfer your information to other countries, we implement
            appropriate safeguards to ensure that your information remains
            protected in accordance with this Privacy Policy and applicable
            laws.
          </Typography>
        </section>

        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            10. Cookies and Tracking Technologies
          </Typography>
          <Typography paragraph>
            We use cookies and similar tracking technologies to track activity
            on our platform and hold certain information. Cookies are files with
            a small amount of data that may include an anonymous unique
            identifier.
          </Typography>
          <Typography paragraph>
            You can instruct your browser to refuse all cookies or to indicate
            when a cookie is being sent. However, if you do not accept cookies,
            you may not be able to use some portions of our platform.
          </Typography>
        </section>

        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            11. Children's Privacy
          </Typography>
          <Typography paragraph>
            Our platform is not intended for use by individuals under the age of
            18. We do not knowingly collect personal information from children.
            If you become aware that a child has provided us with personal
            information, please contact us immediately. If we become aware that
            we have collected personal information from children without
            verification of parental consent, we will take steps to remove that
            information from our servers.
          </Typography>
        </section>

        <section style={{ marginBottom: "1.5rem" }}>
          <Typography variant="h6" gutterBottom>
            12. Changes to This Privacy Policy
          </Typography>
          <Typography paragraph>
            We may update our Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page
            and updating the "Last Updated" date. You are advised to review this
            Privacy Policy periodically for any changes. Changes to this Privacy
            Policy are effective when they are posted on this page.
          </Typography>
        </section>

        <Typography variant="h6" gutterBottom>
          13. Contact Us
        </Typography>
        <Typography paragraph>
          If you have any questions or concerns about this Privacy Policy or our
          data practices, please contact us.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyPolicyDialog;
